import axios from 'axios';

import apiClient from '../config/axios.config';
import { SKIP_ANALYTICS_HEADER } from '../constants/reportBugConstants';
import { logger } from '../utils/logger';

/**
 * Mirrors RW-ERP-Service/src/api/reportBug/report-bug.api.ts → createBugTask().
 * POST /v1/asana/task with the Asana task payload (title, projects[], description,
 * customFields). Returns the raw axios response — callers check `response.status`.
 */

export interface AsanaTaskCustomFields {
  /** Severity (priority). */
  [SUBMIT_TICKET_CUSTOM_FIELD_PRIORITY: string]: string | null;
}

export interface AsanaTaskPayload {
  title: string;
  projects: string[];
  description: string;
  /**
   * Optional: the automatic error-analytics reporter (`bugLogger.ts`) targets the fixed
   * "Error Log" project, which does not have the same custom fields (priority/type/team/
   * domain) configured as the QA/Prod projects the manual Submit Ticket flow uses — sending
   * those field GIDs to a project that doesn't have them causes Asana to reject the task.
   * Omit this field entirely (rather than sending `{}`) when the target project has no
   * matching custom fields.
   */
  customFields?: Record<string, string | null>;
}

export async function createBugTask(payload: AsanaTaskPayload): Promise<{
  status: number;
  data?: unknown;
}> {
  try {
    const response = await apiClient.post('/api/v1/asana/task', payload, {
      headers: { [SKIP_ANALYTICS_HEADER]: 'true' },
    });
    return { status: response.status, data: response.data };
  } catch (error) {
    logger.error('createBugTask failed', error);
    throw error;
  }
}

/**
 * Mirrors RW-ERP-Service/src/components/common/ReportBug.vue → submitEditedImg(),
 * which calls the shared `productFileUpload` (POST `/v1/common/s3/upload`) so the
 * ticket description can link to a real, reachable screenshot instead of embedding
 * the raw data URL as text. Vue hardcodes the `rwdomain` bucket for report-bug
 * screenshots regardless of environment (unlike its env-aware uploads elsewhere) —
 * preserved here for parity.
 */
const REPORT_BUG_S3_UPLOAD_ENDPOINT = '/api/v1/common/s3/upload';
const REPORT_BUG_S3_BUCKET_PATH = 'rwdomain';
const REPORT_BUG_S3_KEY_PREFIX = 'Report-Bug/';

interface ScreenshotUploadResponse {
  status?: boolean;
  /** Carries the uploaded object's URL on success. */
  message?: string;
}

/** Strips the `data:image/png;base64,` prefix off a canvas `toDataURL()` result. */
function dataUrlToBase64(dataUrl: string): string {
  const commaIndex = dataUrl.indexOf(',');
  return commaIndex >= 0 ? dataUrl.slice(commaIndex + 1) : dataUrl;
}

/**
 * Uploads a captured/annotated report-bug screenshot (PNG data URL) to S3 and
 * resolves to its public URL.
 */
export async function uploadReportBugScreenshot(dataUrl: string): Promise<string> {
  const fileKey = `${REPORT_BUG_S3_KEY_PREFIX}Report_Bug_${Date.now()}.png`;

  try {
    const response = await apiClient.post<ScreenshotUploadResponse>(
      REPORT_BUG_S3_UPLOAD_ENDPOINT,
      {
        encodedString: dataUrlToBase64(dataUrl),
        bucketPath: REPORT_BUG_S3_BUCKET_PATH,
        fileKey,
        operation: 'upload',
        access: null,
      },
    );

    const result = response.data;
    if (result?.status === false || !result?.message) {
      throw new Error('Report-bug screenshot upload returned no URL');
    }
    return result.message;
  } catch (error) {
    const message = error instanceof Error ? error.message : 'Upload failed';
    const status = axios.isAxiosError(error) ? error.response?.status : undefined;
    logger.error('uploadReportBugScreenshot failed', { message, status });
    throw error;
  }
}
